import React, { useCallback, useEffect, useState } from 'react';
import { Button, Card, List, Spin, Typography, message } from 'antd';
import { Link } from 'react-router-dom';
import { tripShareEn as t } from '../../content/tripShare.en';
import {
  acceptTripShare,
  activeMemberships,
  declineOrLeaveTripShare,
  listAllTripShares,
  pendingMemberships,
  type TripMembership,
  TripShareApiError,
} from '../../services/tripShareService';
import { setActiveSharedTripIdsFromMemberships } from '../../utils/tripAccessPolicy';
import { pullActiveSharedMemberships } from '../../services/sharedTripSyncService';
import { mergeSharedTripRecordsIntoLocalStore } from '../../services/sharedTripLocalMerge';
import { getAccessibleTrip, getAllTrips, getUserIdForTripWrites, tripWebHref } from '../../services/tripsService';

function sharedTripHref(tripId: string): string {
  const userId = getUserIdForTripWrites();
  const trip = userId ? getAccessibleTrip(userId, tripId) : null;
  return trip && userId ? tripWebHref(userId, trip) : `/trips/${tripId}`;
}

const TripShareInbox: React.FC = () => {
  const [items, setItems] = useState<TripMembership[]>([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await listAllTripShares();
      setItems(rows);
      setActiveSharedTripIdsFromMemberships(rows);
      try {
        const userId = getUserIdForTripWrites();
        const localTripIds = new Set(
          userId ? getAllTrips(userId).map((t) => String(t.id).toLowerCase()) : []
        );
        const pulls = await pullActiveSharedMemberships(activeMemberships(rows), {
          localTripIds,
        });
        for (const pull of pulls) {
          mergeSharedTripRecordsIntoLocalStore(pull.canonicalTripId, pull.records);
        }
      } catch {
        /* sync best-effort */
      }
    } catch (e) {
      message.error(e instanceof Error ? e.message : t.retry);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const onAccept = async (m: TripMembership): Promise<void> => {
    try {
      await acceptTripShare(m.id);
      message.success(t.accepted);
      await reload();
    } catch (e) {
      if (e instanceof TripShareApiError && e.status === 409) {
        message.error(t.invitationUnavailable);
      } else {
        message.error(e instanceof Error ? e.message : t.retry);
      }
    }
  };

  const onDeclineOrLeave = async (m: TripMembership): Promise<void> => {
    try {
      await declineOrLeaveTripShare(m.id);
      message.success(t.left);
      await reload();
    } catch (e) {
      message.error(e instanceof Error ? e.message : t.retry);
    }
  };

  const pending = pendingMemberships(items);
  const active = activeMemberships(items);

  if (loading) {
    return <Spin tip={t.loading} style={{ margin: 48 }} />;
  }

  if (items.length === 0) {
    return (
      <Card title={t.inboxTitle} style={{ margin: 24 }}>
        <Typography.Paragraph type="secondary">{t.inboxEmpty}</Typography.Paragraph>
      </Card>
    );
  }

  return (
    <div style={{ margin: 24, display: 'grid', gap: 24 }}>
      <Typography.Title level={3}>{t.inboxTitle}</Typography.Title>
      {pending.length > 0 && (
        <Card title={t.pendingSection}>
          <List
            dataSource={pending}
            renderItem={(m) => (
              <List.Item
                actions={[
                  <Button key="a" type="primary" onClick={() => void onAccept(m)}>
                    {t.accept}
                  </Button>,
                  <Button key="d" onClick={() => void onDeclineOrLeave(m)}>
                    {t.decline}
                  </Button>,
                ]}
              >
                <List.Item.Meta
                  title={m.tripName || t.unnamedTrip}
                  description={`${t.from} ${m.authorSub.slice(0, 8)}… · ${m.role}`}
                />
              </List.Item>
            )}
          />
        </Card>
      )}
      {active.length > 0 && (
        <Card title={t.sharedWithMe}>
          <List
            dataSource={active}
            renderItem={(m) => (
              <List.Item
                actions={[
                  <Link key="open" to={sharedTripHref(m.tripId)}>
                    <Button type="link">Open</Button>
                  </Link>,
                  <Button key="leave" danger onClick={() => void onDeclineOrLeave(m)}>
                    {t.leave}
                  </Button>,
                ]}
              >
                <List.Item.Meta
                  title={m.tripName || t.unnamedTrip}
                  description={`${m.role} · ${m.status}`}
                />
              </List.Item>
            )}
          />
        </Card>
      )}
    </div>
  );
};

export default TripShareInbox;
